<template>
    <div class="tab-content">
        <div class="image-process-container">
            <div class="file-upload-container">
                <div
                    class="drop-zone"
                    :class="{ highlight: isHighlighted }"
                    @dragover.prevent="highlightDropzone"
                    @dragleave="unhighlightDropzone"
                    @drop="handleDrop"
                >
                    <div v-if="imagePreview" class="image-preview">
                        <img :src="imagePreview" alt="Preview" />
                        <button class="remove-preview" @click="removePreview">×</button>
                    </div>
                    <template v-else>
                        <p>Перетащите изображение сюда или</p>
                        <button class="btn secondary" @click="triggerFileInput">
                            Выберите файл
                        </button>
                        <input
                            ref="fileInput"
                            type="file"
                            accept="image/jpeg,image/png"
                            style="display: none"
                            @change="handleFileSelect"
                        />
                        <p>Максимальный размер: 5MB</p>
                    </template>
                </div>

                <div class="or-separator">
                    <span>ИЛИ</span>
                </div>

                <div class="url-input-group">
                    <input
                        v-model="imageUrl"
                        type="text"
                        placeholder="Вставьте URL изображения"
                        @input="handleUrlInput"
                    />
                </div>
            </div>

            <div class="controls">
                <div class="input-group">
                    <label for="materialsLimit">Лимит материалов</label>
                    <input
                        id="materialsLimit"
                        v-model.number="materialsLimit"
                        type="number"
                        min="1"
                        placeholder="5"
                    />
                </div>

                <button class="btn" :disabled="!processEnabled || processing" @click="processImage">
                    <span v-if="processing">Обработка...</span>
                    <span v-else>Обработать изображение</span>
                </button>
            </div>
        </div>

        <div class="preview-area">
            <div class="image-preview-card">
                <div class="image-preview-header">Предпросмотр изображения</div>
                <div class="image-preview-content">
                    <img :src="sidebarImage" alt="Image preview" />
                </div>
            </div>

            <div id="result" class="results-container">
                <div v-if="processing" class="loading">
                    <div class="spinner"></div>
                    <p>Обработка изображения...</p>
                </div>

                <div v-else-if="errorMessage" class="error">
                    {{ errorMessage }}
                </div>

                <template v-else-if="results.length">
                    <div v-for="(room, index) in results" :key="index" class="room">
                        <div class="room-header">
                            <h2 class="room-title">
                                Комната {{ index + 1 }} ({{ room.room_type }})
                            </h2>
                            <div class="chance">Вероятность: {{ room.chance }}%</div>
                        </div>

                        <div v-if="room.ceiling" class="section">
                            <h3 class="section-title">Потолок</h3>
                            <RoomSection :section-data="room.ceiling" />
                        </div>

                        <div v-if="room.floor" class="section">
                            <h3 class="section-title">Пол</h3>
                            <RoomSection :section-data="room.floor" />
                        </div>

                        <div v-if="room.walls" class="section">
                            <h3 class="section-title">Стены</h3>
                            <RoomSection :section-data="room.walls" />
                        </div>
                    </div>
                </template>

                <div v-else class="no-results">
                    <p>Загрузите изображение для обработки</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, computed } from 'vue'
    import api from '@/services/api'
    import RoomSection from '@/components/RoomSection.vue'

    export default {
        components: { RoomSection },
        setup() {
            const fileInput = ref(null)
            const imagePreview = ref(null)
            const imageUrl = ref('')
            const sidebarImage = ref('')
            const materialsLimit = ref(5)
            const results = ref([])
            const isHighlighted = ref(false)
            const processing = ref(false)
            const errorMessage = ref('')

            const processEnabled = computed(() => {
                return imagePreview.value || imageUrl.value.trim()
            })

            function triggerFileInput() {
                if (fileInput.value) {
                    fileInput.value.click()
                }
            }

            function handleFileSelect(event) {
                const file = event.target.files[0]
                if (file) {
                    handleFile(file)
                }
            }

            function handleFile(file) {
                if (file.size > 5 * 1024 * 1024) {
                    alert('Файл слишком большой! Максимальный размер 5MB')
                    return
                }

                const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
                if (!validTypes.includes(file.type)) {
                    alert('Неподдерживаемый формат! Используйте JPG или PNG')
                    return
                }

                imagePreview.value = URL.createObjectURL(file)
                sidebarImage.value = imagePreview.value
                imageUrl.value = ''
            }

            function handleDrop(event) {
                event.preventDefault()
                unhighlightDropzone()

                const files = event.dataTransfer.files
                if (files && files.length > 0) {
                    handleFile(files[0])
                }
            }

            function highlightDropzone() {
                isHighlighted.value = true
            }

            function unhighlightDropzone() {
                isHighlighted.value = false
            }

            function removePreview() {
                imagePreview.value = null
                sidebarImage.value = ''
                if (fileInput.value) {
                    fileInput.value.value = ''
                }
            }

            function handleUrlInput() {
                if (imageUrl.value.trim()) {
                    sidebarImage.value = imageUrl.value
                    imagePreview.value = null
                    if (fileInput.value) {
                        fileInput.value.value = ''
                    }
                } else {
                    sidebarImage.value = ''
                }
            }

            async function processImage() {
                processing.value = true
                errorMessage.value = ''
                results.value = []

                try {
                    const formData = new FormData()

                    if (imagePreview.value) {
                        const blob = await fetch(imagePreview.value).then(r => r.blob())
                        formData.append('image', blob, 'image.jpg')
                    } else if (imageUrl.value) {
                        formData.append('image_url', imageUrl.value)
                    }

                    formData.append('materials_limit', materialsLimit.value.toString())

                    const data = await api.processImage(formData)
                    results.value = data
                } catch (error) {
                    console.error('Image processing error:', error)
                    errorMessage.value =
                        'Произошла ошибка при обработке изображения: ' +
                        (error.response?.data?.detail || error.message)
                } finally {
                    processing.value = false
                }
            }

            return {
                fileInput,
                imagePreview,
                imageUrl,
                sidebarImage,
                materialsLimit,
                results,
                isHighlighted,
                processing,
                errorMessage,
                processEnabled,
                triggerFileInput,
                handleFileSelect,
                handleDrop,
                highlightDropzone,
                unhighlightDropzone,
                removePreview,
                handleUrlInput,
                processImage
            }
        }
    }
</script>

<style scoped>
    .image-process-container {
        background: #f8faff;
        padding: 20px;
        border-radius: 10px;
        margin-bottom: 30px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .file-upload-container {
        margin-bottom: 20px;
    }

    .drop-zone {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        border: 2px dashed #d0d9e7;
        border-radius: 8px;
        padding: 30px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        margin-bottom: 20px;
    }

    .drop-zone:hover,
    .drop-zone.highlight {
        border-color: var(--primary-color);
        background: rgba(74, 108, 247, 0.05);
    }

    .or-separator {
        position: relative;
        text-align: center;
        margin: 20px 0;
    }

    .or-separator::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 1px;
        background: var(--border-color);
        z-index: 1;
    }

    .or-separator span {
        position: relative;
        z-index: 2;
        background: white;
        padding: 0 15px;
        color: var(--text-light);
    }

    .url-input-group {
        margin-bottom: 20px;
    }

    .url-input-group input {
        width: 100%;
        padding: 12px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        box-sizing: border-box;
    }

    .controls {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        align-items: flex-end;
    }

    .input-group {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 200px;
    }

    .input-group label {
        margin-bottom: 8px;
        font-weight: 600;
        color: var(--text-dark);
    }

    .input-group input {
        padding: 12px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        width: 100%;
        box-sizing: border-box;
    }

    .preview-area {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 25px;
        margin-top: 30px;
    }

    .image-preview-card {
        background: var(--card-bg);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        border: 1px solid #eaeff5;
        height: fit-content;
    }

    .image-preview-header {
        padding: 15px 20px;
        border-bottom: 1px solid #f0f0f0;
        font-weight: 600;
        color: var(--text-dark);
    }

    .image-preview-content {
        padding: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .image-preview-content img {
        max-width: 100%;
        max-height: 400px;
        border-radius: 8px;
    }

    .results-container {
        display: flex;
        flex-direction: column;
        gap: 25px;
    }

    .room {
        background: var(--card-bg);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        border: 1px solid #eaeff5;
    }

    .room:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    }

    .room-header {
        padding: 15px 20px;
        border-bottom: 1px solid #f0f0f0;
    }

    .room-title {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
        color: var(--text-dark);
    }

    .chance {
        font-size: 14px;
        color: var(--text-light);
        margin-top: 5px;
    }

    .section {
        padding: 15px 20px;
        border-bottom: 1px solid #f0f0f0;
    }

    .section:last-child {
        border-bottom: none;
    }

    .section-title {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 10px;
        color: var(--text-dark);
    }

    .loading,
    .error,
    .no-results {
        text-align: center;
        padding: 40px;
        font-size: 18px;
        grid-column: 1 / -1;
    }

    .error {
        color: var(--error-color);
    }

    .image-preview {
        position: relative;
        max-height: 350px;
        max-width: 100%;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        margin: 0 auto 15px;
        aspect-ratio: 16/9;
    }

    .image-preview img {
        max-height: 100%;
        max-width: 100%;
        object-fit: contain;
        width: auto;
        height: auto;
        display: block;
    }

    .remove-preview {
        position: absolute;
        bottom: -15px;
        background: rgba(220, 53, 69, 0.8);
        color: white;
        border: none;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        cursor: pointer;
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .spinner {
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        border-top: 4px solid var(--primary-color);
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 992px) {
        .preview-area {
            grid-template-columns: 1fr;
        }
    }

    @media (max-width: 768px) {
        .controls {
            flex-direction: column;
        }

        .input-group {
            min-width: 100%;
        }
    }

    @media (max-width: 480px) {
        .image-process-container {
            padding: 15px;
        }
    }
</style>
